// ============================================
// WITHDRAWAL HOOKS
// Combines hooks from:
// - [BuilderWithdrawalHelpers.tsx] -> [Withdrawal.tsx] Withdrawal (3404-3407)
// - [BuilderWithdrawalSources.tsx] -> [BuilderWithdrawalHelpers.tsx] -> [Withdrawal.tsx] Withdrawal (300-303)
// - [BuilderWithdrawalSourcesHelpers.tsx] -> [BuilderWithdrawalSources.tsx] -> Withdrawal (304-314)
// - [NewBuilderCurrentAccountDetails.tsx] -> [BuilderWithdrawalHelpers.tsx] -> Withdrawal (351-359)
// ============================================

import React, {useEffect} from 'react';
import {FieldInputProps} from 'formik';
import moment, {MomentInput} from 'moment';
import {SelectGroupOption} from '../../../components/AutoForm/AutoFieldNew';
import FREQUENCY, {FREQUENCY_COMBOS, toFrequency} from '../../../constants/Frequency';
import {OVRecommendActionEnum, BankDetailsEnum} from '../../../schema-types/globalTypes';
import OWNERSHIP from '../../../constants/Ownership';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {withdrawalFieldsType} from '../BuilderWithdrawal/builderWithdrawalFields';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import loadMomentDate from '../../../utils/loadMomentDate';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {getWithdrawalMinimumDate} from '../BuilderWithdrawal/builderGetWithdrawalMinimumDate';
import {FULL_SURRENDER} from '../BuilderWithdrawal/builderWithdrawalFormFields';
import {includesSaltus} from '../../../utils/includesSaltus';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {NEW_BANK_ACCOUNT_ID} from '../CreateBuilder/BuilderSourceDestination/builderConstants';
import {UPDATES} from '../../../shared/UPDATES';
import {findParentOfChild} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';

// ============================================
// BuilderWithdrawalHelpers.tsx (3404-3407)
// ============================================

// Shared defaults for `withdraw.source` — used by the change factory and the load path.
export const changeWithdrawalSource = ({
  pot,
  parentPotId,
  carryOver,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
  parentPotId?: string;
  carryOver?: withdrawalFieldsType['source'];
}): {
  source: withdrawalFieldsType['source'];
  pot: BuilderPot | BuilderPot_dentistChildren;
} => {
  return {
    source: {
      frequency: FREQUENCY.SINGLE,
      ...carryOver,
      id: pot.id,
      parentPotId,
      singleClientAmount: 0,
      maxAmount: getCurrencyFloat(pot.totalValue),
      isSaltus: includesSaltus(pot.productProvider?.formattedName),
      crystallisedFunds: pot.crystallisedFunds,
      uncrystallisedFunds: pot.uncrystallisedFunds,
    },
    pot,
  };
};

export const changeWithdrawalSourceFactory = ({
  sourcePots,
  withdraw,
  withdrawSourceHelpers,
}: {
  sourcePots: BuilderPot[];
  withdraw: withdrawalFieldsType | undefined;
  withdrawSourceHelpers: AutoFieldHelpers<withdrawalFieldsType['source']>;
}) => {
  return (value: string) => {
    const parent = findParentOfChild(sourcePots, value);
    if (parent) {
      const child = parent.dentistChildren?.find(
        (c: BuilderPot_dentistChildren) => c.id === value
      );
      if (!child) return;
      const {source} = changeWithdrawalSource({
        pot: child,
        parentPotId: parent.id,
        carryOver: withdraw?.source,
      });
      withdrawSourceHelpers.setValue(UPDATES._3404, source);
      return;
    }
    // Leaf pot (no dentist children) — use the pot directly
    const leafPot = sourcePots.find((p) => p.id === value);
    if (!leafPot) return;
    const {source} = changeWithdrawalSource({
      pot: leafPot,
      parentPotId: undefined,
      carryOver: withdraw?.source,
    });
    withdrawSourceHelpers.setValue(UPDATES._3404, source);
  };
};

// Build {id, parentPotId} from a selected destination pot or child.
export const getWithdrawalDestinationFromSelection = ({
  pot,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
}): {id: string; parentPotId?: string} => {
  if ('dentistChildren' in pot) {
    // Bank account or non-bank leaf — parent selected directly, no parentPotId
    return {id: pot.id, parentPotId: undefined};
  }
  return {id: pot.id, parentPotId: pot.parent?.id ?? undefined};
};

export const changeWithdrawalDestinationFactory = ({
  withdraw,
  withdrawDestinationHelpers,
}: {
  withdraw: withdrawalFieldsType | undefined;
  withdrawDestinationHelpers: AutoFieldHelpers<withdrawalFieldsType['destination']>;
}) => {
  return (pot: BuilderPot | BuilderPot_dentistChildren) => {
    const {id, parentPotId} = getWithdrawalDestinationFromSelection({pot});
    withdrawDestinationHelpers.setValue(UPDATES._3405, {
      ...withdraw?.destination,
      id,
      parentPotId,
    });
  };
};

// NEW_BANK_ACCOUNT_ID flow — separate, since BuilderWithdrawalNewAccountPlanForm has no pot object.
export const selectNewBankDestinationFactory = ({
  withdraw,
  withdrawDestinationHelpers,
}: {
  withdraw: withdrawalFieldsType | undefined;
  withdrawDestinationHelpers: AutoFieldHelpers<withdrawalFieldsType['destination']>;
}) => {
  return () => {
    withdrawDestinationHelpers.setValue(UPDATES._3405, {
      ...withdraw?.destination,
      id: NEW_BANK_ACCOUNT_ID,
      parentPotId: undefined,
    });
  };
};

export const loadSavedWithdrawal = ({
  builderDetails,
  withdrawHelpers,
}: {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  withdrawHelpers: AutoFieldHelpers<withdrawalFieldsType | undefined>;
}) => {
  if (!builderDetails) {
    return;
  }

  const savedWithdrawal = getSavedWithdrawalValues(builderDetails);
  if (!savedWithdrawal) return;
  withdrawHelpers.setValue(UPDATES._3407, {
    ...savedWithdrawal,
  });
};

// ============================================
// BuilderWithdrawalSources.tsx (300-303)
// ============================================

export const handleWithdrawalFullAndReason = ({
  withdrawalValues,
  values,
  options,
  setFieldValue,
  setFullAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: builderFieldsType;
  options: SelectGroupOption[];
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  setFullAmount: () => void;
}) => {
  if (withdrawalValues.source.full) {
    setFieldValue(UPDATES._300, 'withdraw.source.reason', FULL_SURRENDER);
    setFieldValue(UPDATES._301, 'withdraw.source.frequency', FREQUENCY.SINGLE);
    setFullAmount();
    return;
  }
  if (options.length && options[0]) {
    if (!values.withdraw?.source.reason) {
      setFieldValue(UPDATES._303, 'withdraw.source.reason', null);
    }
    const reasonIsInOptions = options.find((x) =>
      x.options.find((y) => y.value === values.withdraw?.source.reason)
    );
    if (!reasonIsInOptions) {
      setFieldValue(UPDATES._303, 'withdraw.source.reason', null);
    }
  }
};

export const useHandleWithdrawalFullAndReason = ({
  options,
  setFullAmount,
}: {
  options: SelectGroupOption[];
  setFullAmount: () => void;
}) => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const withdrawalValues = values.withdraw!;
  useEffect(() => {
    handleWithdrawalFullAndReason({
      withdrawalValues,
      values,
      options,
      setFieldValue,
      setFullAmount,
    });
  }, [options, values.withdraw?.source.reason, withdrawalValues.source.full]);
};

// ============================================
// BuilderWithdrawalSourcesHelpers.tsx (304-314)
// ============================================

export const setWithdrawalFullAmountFactory = ({
  withdrawalValues,
  totalAmount,
  setAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  totalAmount: number | undefined;
  setAmount: AutoFieldHelpers<moneyFieldValueType>['setValue'];
}) => {
  return () => {
    if (withdrawalValues.source?.full) {
      setAmount(UPDATES._306, totalAmount || '');
      withdrawalValues.source.singleClientAmount = totalAmount || '';
    } else {
      setAmount(UPDATES._307, withdrawalValues.source?.singleClientAmount);
    }
  };
};

export const setStartDateMustBe10daysInTheFuture = ({
  withdrawalValues,
  values,
  setFieldValue,
  today, // need to pass this in for testing
  builderDetails,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: builderFieldsType;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  today: MomentInput;
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
}) => {
  if (
    withdrawalValues.source?.start ||
    values.withdraw?.source?.frequency === FREQUENCY.SINGLE
  ) {
    return;
  }
  const currentOrSavedDate = withdrawalValues.source?.start || builderDetails?.startDate;
  const minimumDate = getWithdrawalMinimumDate(today, moment(currentOrSavedDate));

  setFieldValue(
    UPDATES._304,
    'withdraw.source.start',
    // withdrawal start date can only be at least 10 days in the future
    minimumDate,
    true
  );
};

export const getSavedWithdrawalValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): Omit<withdrawalFieldsType, 'planProvider'> | undefined => {
  if (!builderDetails?.withdraw) return undefined;
  const savedSource = builderDetails?.pots?.find((p) => p?.planType === 'source');

  const frequency = toFrequency(builderDetails?.pots?.[0]?.recurringClientFrequency);

  const source: withdrawalFieldsType['source'] = {
    frequency,
    singleClientAmount: getCurrencyFloat(savedSource?.singleClientAmount) || '',
    id: savedSource?.pot?.id,
    parentPotId: savedSource?.pot?.parent?.id ?? undefined,
    reason:
      savedSource?.pot.withdrawalReason || builderDetails?.withdrawalReason || undefined,
    recurringClientAmount: getCurrencyFloat(savedSource?.recurringClientAmount),
    maxAmount: savedSource?.pot.totalValue,
    full: savedSource?.full || false,
    isSaltus: includesSaltus(savedSource?.pot.productProvider?.formattedName),
    crystallisedFunds: savedSource?.pot.crystallisedFunds,
    uncrystallisedFunds: savedSource?.pot.uncrystallisedFunds,
    start: loadMomentDate(
      builderDetails?.startDate ? String(builderDetails?.startDate) : undefined
    ),
  };

  const initialFee = savedSource?.initialFee?.id;

  // only selecting from list if there is saved data
  const savedDestination = builderDetails?.pots?.find(
    (p) => p?.planType === 'destination'
  );
  // cannot find this plan in destinations? then it's a new one
  const isBuilderNewPlan =
    savedDestination?.pot.productIsBank && !savedDestination.pot.curoId;

  const destination: withdrawalFieldsType['destination'] | undefined = savedDestination
    ? isBuilderNewPlan
      ? {
          id: NEW_BANK_ACCOUNT_ID,
          parentPotId: undefined,
          newExistingPotId: savedDestination.pot.id,
          sortCode: savedDestination.pot.sortCode || undefined,
          accountNumber: savedDestination.pot.planNumber || undefined,
          productProvider: savedDestination.pot.productProvider?.id,
          accountName: savedDestination.pot.policyHolder || '',
          bankDetails: savedDestination.pot.bankDetails,
          contactId: !savedDestination.pot.contact?.id
            ? OWNERSHIP.JOINT_CODE
            : savedDestination.pot.contact?.id,
        }
      : {
          id: savedDestination.pot.id,
          parentPotId: savedDestination.pot.parent?.id ?? undefined,
        }
    : undefined;

  return {source, initialFee, ...(destination ? {destination} : {})};
};

// export const loadSavedWithdrawalSource = ({
//   builderDetails,
//   setFieldValue,
// }: {
//   builderDetails: OvbuilderDetails_ovrecommendDetails | null;
//   setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
// }) => {
//   if (!builderDetails) {
//     setFieldValue(UPDATES._301, 'withdraw.frequency', FREQUENCY.SINGLE, true);
//     return;
//   }
//
//   const frequency = builderDetails?.pots?.[0]?.recurringClientFrequency
//     ? builderDetails.pots[0].recurringClientFrequency
//     : FREQUENCY.SINGLE;
//   setFieldValue(UPDATES._308, 'withdraw.frequency', frequency, true);
//   setFieldValue(
//     UPDATES._310,
//     'withdraw.source.start',
//     loadMomentDate(
//       builderDetails?.startDate ? String(builderDetails?.startDate) : undefined
//     ),
//     true
//   );
//   const sourcePot = builderDetails.pots?.find((x) => x.planType === 'source');
//   if (!sourcePot) {
//     return;
//   }
//   setFieldValue(
//     UPDATES._311,
//     'withdraw.source.reason',
//     sourcePot.pot.withdrawalReason || builderDetails?.withdrawalReason
//   );
//   setFieldValue(
//     UPDATES._312,
//     'withdraw.source.singleClientAmount',
//     getCurrencyFloat(sourcePot?.singleClientAmount)
//   );
//   setFieldValue(
//     UPDATES._313,
//     'withdraw.source.recurringClientAmount',
//     getCurrencyFloat(sourcePot.recurringClientAmount)
//   );
//
//   setFieldValue(UPDATES._314, 'withdraw.initialFee', sourcePot.initialFee?.id, true);
// };

// ============================================
// NewBuilderCurrentAccountDetails.tsx (351-359)
// ============================================

export const loadNewCurrentAccount = ({
  builderDetails,
  setFieldValue,
}: {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
}) => {
  if (!builderDetails) {
    return;
  }
  const destinationPot = builderDetails?.pots?.find(
    (x) => x?.planType === 'destination'
  )?.pot;

  if (
    destinationPot?.sortCode === '000000' ||
    destinationPot?.planNumber === '00000000' ||
    !destinationPot?.sortCode ||
    !destinationPot?.planNumber
  ) {
    setFieldValue(
      UPDATES._357,
      'withdraw.destination.bankDetails',
      BankDetailsEnum.NOT_KNOWN
    );
  } else {
    setFieldValue(
      UPDATES._356,
      'withdraw.destination.bankDetails',
      BankDetailsEnum.NEED_VALIDATING
    );
  }
  // if (!builderDetails.contact?.id) {
  //   setFieldValue(
  //     UPDATES._352,
  //     `${PLAN_ACTION.WITHDRAW}.destination.ownership`,
  //     OWNERSHIP.JOINT_CODE
  //   );
  // }
  setFieldValue(
    UPDATES._353,
    'withdraw.destination.productProvider',
    destinationPot?.productProvider?.id
  );
  setFieldValue(
    UPDATES._351,
    'withdraw.destination.accountNumber',
    destinationPot?.planNumber || ''
  );
  setFieldValue(
    UPDATES._354,
    'withdraw.destination.accountName',
    destinationPot?.policyHolder
  );
  setFieldValue(UPDATES._355, 'withdraw.destination.sortCode', destinationPot?.sortCode);
};
